import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faInbox, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import ReactPaginate from "react-paginate";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { ProductContext } from "../../../store";
import { PostData } from "../../../services/FetchData";
import "./AIAssistant.css";

const AIAssistantWebsiteMetadata = () => {
	const { currentWebsiteId } = useContext(ProductContext);
	const fileInputRef = useRef(null);

	const [pageLoader, setPageLoader] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [rows, setRows] = useState([]);
	const [totalRows, setTotalRows] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);
	const [itemsPerPage] = useState(10);
	const [selectedFile, setSelectedFile] = useState(null);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [rowToDelete, setRowToDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const getWebsiteId = useCallback(() => currentWebsiteId || Cookies.get("_website_id"), [currentWebsiteId]);

	const fetchMetadata = useCallback(async () => {
		const websiteId = getWebsiteId();
		if (!websiteId) {
			setRows([]);
			setTotalRows(0);
			setPageLoader(false);
			return;
		}

		setPageLoader(true);
		try {
			const payload = {
				websiteId,
				page: currentPage + 1,
				limit: itemsPerPage,
				searchTerm: "",
			};
			const response = await PostData("ms1", "api/v1/aiassistant/website-metadata/list", payload);
			if (response !== "Invalid" && response !== undefined && response.status === "success") {
				const listData =
					response?.data?.items ||
					response?.data?.rows ||
					response?.data?.websiteMetadata ||
					response?.data?.metadata ||
					[];
				const total = response?.data?.total || response?.data?.count || listData.length;
				setRows(Array.isArray(listData) ? listData : []);
				setTotalRows(total);
			} else {
				toast.error(response?.message?.message || response?.message || "Failed to fetch website metadata");
				setRows([]);
				setTotalRows(0);
			}
		} catch (error) {
			toast.error("An error occurred while fetching website metadata");
			setRows([]);
			setTotalRows(0);
		} finally {
			setPageLoader(false);
		}
	}, [currentPage, getWebsiteId, itemsPerPage]);

	useEffect(() => {
		fetchMetadata();
	}, [fetchMetadata]);

	const handleFileChange = (event) => {
		const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
		setSelectedFile(file);
	};

	const handleUpload = async () => {
		const websiteId = getWebsiteId();
		if (!websiteId) {
			toast.error("Website ID is missing. Please refresh and try again.");
			return;
		}
		if (!selectedFile) {
			toast.error("Please choose a file to upload.");
			return;
		}

		const formData = new FormData();
		formData.append("websiteId", String(websiteId));
		formData.append("file", selectedFile);

		setUploading(true);
		try {
			const response = await PostData("ms1", "api/v1/aiassistant/website-metadata/upload", formData, true);
			if (response !== "Invalid" && response !== undefined && response.status === "success") {
				toast.success(response?.message || "File uploaded successfully");
				setSelectedFile(null);
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}
				setIsUploadModalOpen(false);
				fetchMetadata();
			} else {
				toast.error(response?.message?.message || response?.message || "Failed to upload file");
			}
		} catch (error) {
			toast.error("An error occurred while uploading file");
		} finally {
			setUploading(false);
		}
	};

	const handlePageClick = (data) => {
		setCurrentPage(data.selected);
	};

	const handleDeleteClick = (row) => {
		setRowToDelete(row);
		setIsDeleteModalOpen(true);
	};

	const handleDelete = async () => {
		const websiteId = getWebsiteId();
		const websiteMetadataId = rowToDelete?.websiteMetadataId || rowToDelete?.id;
		if (!websiteId || !websiteMetadataId) {
			toast.error("Website metadata identifier is missing.");
			return;
		}

		setDeleting(true);
		try {
			const payload = {
				websiteId,
				websiteMetadataId,
			};
			const response = await PostData("ms1", "api/v1/aiassistant/website-metadata/delete", payload);
			if (response !== "Invalid" && response !== undefined && response.status === "success") {
				toast.success(response?.message || "Website metadata deleted successfully");
				setIsDeleteModalOpen(false);
				setRowToDelete(null);
				fetchMetadata();
			} else {
				toast.error(response?.message?.message || response?.message || "Failed to delete website metadata");
			}
		} catch (error) {
			toast.error("An error occurred while deleting website metadata");
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className="ai-assistant-website-metadata">
			<div className="d-flex justify-content-between align-items-start mb-4">
				<div>
					<h2 className="mb-1">Shopping Training</h2>
					<p className="text-muted mb-0">Upload files and review shopping training records.</p>
				</div>
				<Button color="primary" className="conversionBox" onClick={() => setIsUploadModalOpen(true)}>
					<FontAwesomeIcon icon={faUpload} className="me-2" />
					Add Training Document
				</Button>
			</div>

			{pageLoader ? (
				<DetailSkeleton />
			) : (
				<div className="table-responsive">
					<Table className="data-table website-metadata-table">
						<thead>
							<tr>
								<th>File Type</th>
								<th>File Name</th>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{rows.length > 0 ? (
								rows.map((row, index) => {
									const fileType = row.fileType || row.file_type || "";
									const fileName = row.fileName || row.file_name || "";
									const key = row.id || `${fileName}-${index}`;

									return (
										<tr key={key}>
											<td>{fileType || "N/A"}</td>
											<td>{fileName || "N/A"}</td>
											<td>
												<Button
													color="link"
													className="p-0 text-danger"
													title="Delete metadata"
													aria-label="Delete metadata"
													onClick={() => handleDeleteClick(row)}
												>
													<FontAwesomeIcon icon={faTrash} />
												</Button>
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={4} className="text-center p-5">
										<div className="d-flex flex-column align-items-center">
											<FontAwesomeIcon icon={faInbox} style={{ fontSize: "48px", color: "#dee2e6" }} />
											<p className="text-muted mt-3 mb-0">No website metadata found</p>
										</div>
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
			)}

			{totalRows > itemsPerPage && (
				<div className="d-flex justify-content-center mt-4">
					<ReactPaginate
						previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
						nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
						pageCount={Math.ceil(totalRows / itemsPerPage)}
						onPageChange={handlePageClick}
						forcePage={currentPage}
						containerClassName="pagination"
						previousLinkClassName="page-link"
						nextLinkClassName="page-link"
						pageLinkClassName="page-link"
						activeClassName="active"
						disabledClassName="disabled"
					/>
				</div>
			)}

			<Modal isOpen={isUploadModalOpen} toggle={() => !uploading && setIsUploadModalOpen(false)}>
				<ModalHeader toggle={() => !uploading && setIsUploadModalOpen(false)}>Upload File</ModalHeader>
				<ModalBody>
					<Input
						innerRef={fileInputRef}
						type="file"
						onChange={handleFileChange}
						disabled={uploading}
						aria-label="Upload metadata file"
					/>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" outline onClick={() => setIsUploadModalOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button color="primary" className="conversionBox" onClick={handleUpload} disabled={uploading || !selectedFile}>
						{uploading ? "Uploading..." : "Upload File"}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal isOpen={isDeleteModalOpen} toggle={() => !deleting && setIsDeleteModalOpen(false)}>
				<ModalHeader toggle={() => !deleting && setIsDeleteModalOpen(false)}>Delete Metadata</ModalHeader>
				<ModalBody>
					Are you sure you want to delete this metadata record? This action cannot be undone.
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						outline
						onClick={() => {
							setIsDeleteModalOpen(false);
							setRowToDelete(null);
						}}
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button color="danger" onClick={handleDelete} disabled={deleting}>
						{deleting ? "Deleting..." : "Delete"}
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

export default AIAssistantWebsiteMetadata;
